import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LeadCapture() {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    source: 'website',
    notes: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/public/leads`, formData);
      setSubmitted(true);
      toast.success('Lead capturado com sucesso!');
    } catch (error) {
      toast.error('Erro ao enviar formulário. Tente novamente.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
        <div className="absolute inset-0 bg-grid-slate-200 bg-[size:20px_20px] opacity-30"></div>
        
        <Card className="glass shadow-2xl border-white/20 max-w-md w-full relative z-10">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            
            <h2 className="text-2xl font-bold text-slate-800 mb-4">Obrigado!</h2>
            <p className="text-slate-600 mb-6">
              Seu interesse foi registrado com sucesso. Nossa equipe entrará em contato em breve!
            </p>
            
            <div className="space-y-3 text-sm text-slate-500">
              <p>✅ Lead capturado no sistema</p>
              <p>📧 Confirmação enviada por email</p>
              <p>📞 Contato em até 24 horas</p>
            </div>

            <Button 
              onClick={() => {
                setSubmitted(false);
                setFormData({
                  name: '',
                  email: '',
                  phone: '',
                  company: '',
                  source: 'website',
                  notes: ''
                });
              }}
              variant="outline"
              className="mt-6 bg-white/60"
            >
              Enviar Outro Lead
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50"></div>
      <div className="absolute inset-0 bg-grid-slate-200 bg-[size:20px_20px] opacity-30"></div>
      
      {/* Navigation */}
      <nav className="relative z-10 flex items-center justify-between p-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800">NexoSynX</h1>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="relative z-10 max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Side - Content */}
          <div className="space-y-8">
            <div>
              <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
                Transforme
                <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent"> Leads </span>
                em
                <span className="bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text text-transparent"> Vendas</span>
              </h1>
              <p className="text-xl text-slate-600 leading-relaxed">
                Sistema completo de CRM para capturar, qualificar e converter seus leads em clientes fiéis. 
                Aumente suas vendas com automação inteligente.
              </p>
            </div>

            {/* Features */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="glass rounded-xl p-6">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-slate-800 mb-2">Captura Automática</h3>
                <p className="text-slate-600 text-sm">Capture leads automaticamente de múltiplas fontes</p>
              </div>

              <div className="glass rounded-xl p-6">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-slate-800 mb-2">Pipeline Visual</h3>
                <p className="text-slate-600 text-sm">Acompanhe o progresso dos leads visualmente</p>
              </div>

              <div className="glass rounded-xl p-6">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-violet-600 rounded-xl flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-slate-800 mb-2">Follow-up Inteligente</h3>
                <p className="text-slate-600 text-sm">Nunca perca uma oportunidade com lembretes automáticos</p>
              </div>

              <div className="glass rounded-xl p-6">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-slate-800 mb-2">Relatórios Completos</h3>
                <p className="text-slate-600 text-sm">Análises detalhadas para otimizar suas vendas</p>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="lg:pl-8">
            <Card className="glass shadow-2xl border-white/20" data-testid="lead-capture-form">
              <CardHeader className="text-center">
                <CardTitle className="text-2xl text-slate-800">
                  Teste Grátis por 30 Dias
                </CardTitle>
                <CardDescription className="text-base">
                  Comece a capturar e converter leads hoje mesmo
                </CardDescription>
              </CardHeader>
              
              <CardContent className="p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-sm font-medium text-slate-700">
                        Nome Completo *
                      </Label>
                      <Input
                        id="name"
                        type="text"
                        data-testid="capture-name-input"
                        placeholder="Seu nome completo"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        required
                        className="h-12 bg-white/50 border-white/30 focus:bg-white/70"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                        Email Empresarial *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        data-testid="capture-email-input"
                        placeholder="seu@empresa.com"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        required
                        className="h-12 bg-white/50 border-white/30 focus:bg-white/70"
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-sm font-medium text-slate-700">
                        Telefone
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        data-testid="capture-phone-input"
                        placeholder="(11) 99999-9999"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="h-12 bg-white/50 border-white/30 focus:bg-white/70"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="company" className="text-sm font-medium text-slate-700">
                        Nome da Empresa
                      </Label>
                      <Input
                        id="company"
                        type="text"
                        data-testid="capture-company-input"
                        placeholder="Sua empresa"
                        value={formData.company}
                        onChange={(e) => setFormData({...formData, company: e.target.value})}
                        className="h-12 bg-white/50 border-white/30 focus:bg-white/70"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="source" className="text-sm font-medium text-slate-700">
                      Como nos conheceu?
                    </Label>
                    <Select 
                      value={formData.source} 
                      onValueChange={(value) => setFormData({...formData, source: value})}
                    >
                      <SelectTrigger className="h-12 bg-white/50 border-white/30" data-testid="capture-source-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="website">Website/Blog</SelectItem>
                        <SelectItem value="social">Redes Sociais</SelectItem>
                        <SelectItem value="ads">Anúncios Online</SelectItem>
                        <SelectItem value="referral">Indicação</SelectItem>
                        <SelectItem value="email">Email Marketing</SelectItem>
                        <SelectItem value="event">Evento/Webinar</SelectItem>
                        <SelectItem value="other">Outros</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="notes" className="text-sm font-medium text-slate-700">
                      Conte-nos sobre sua necessidade
                    </Label>
                    <Textarea
                      id="notes"
                      data-testid="capture-notes-input"
                      placeholder="Descreva brevemente como podemos ajudar sua empresa..."
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      rows={4}
                      className="bg-white/50 border-white/30 focus:bg-white/70 resize-none"
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    data-testid="capture-submit-button"
                    disabled={loading}
                    className="w-full h-14 btn-primary text-lg font-semibold"
                  >
                    {loading ? (
                      <div className="flex items-center space-x-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        <span>Enviando...</span>
                      </div>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        Começar Teste Grátis
                      </>
                    )}
                  </Button>

                  <div className="text-center">
                    <p className="text-xs text-slate-500">
                      Ao enviar, você concorda com nossos termos de uso e política de privacidade
                    </p>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Trust Indicators */}
            <div className="grid grid-cols-3 gap-4 mt-8 text-center">
              <div className="glass rounded-lg p-4">
                <div className="text-2xl font-bold text-slate-800">99%</div>
                <p className="text-xs text-slate-600">Satisfação</p>
              </div>
              <div className="glass rounded-lg p-4">
                <div className="text-2xl font-bold text-slate-800">24h</div>
                <p className="text-xs text-slate-600">Suporte</p>
              </div>
              <div className="glass rounded-lg p-4">
                <div className="text-2xl font-bold text-slate-800">SSL</div>
                <p className="text-xs text-slate-600">Seguro</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}